import { EQuestionDifficulty, EQuestionType } from '@constants';
import { IQuestion } from '@sdk/contracts';
import { Alert, Button, Collapse, InputNumber, Modal, Tooltip } from 'antd';
import { Form, Input, Radio } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Answers } from './Answers';
import {
  ButtonWrapper,
  FooterWrapper,
  ItemsWrapper,
  Wrapper,
  FormulaWrapper,
  QuizItemWrapper,
  TitleWrapper,
  RowWrapper
} from './ManualQuestionModal.styles';

import { Icon } from '@/components/Icon';
import { InsertFormula } from '@/components/InsertFormula';
import { Upload } from '@/components/Upload';
import { RULES } from '@/constants';
import { useManualQuestionForm } from '@/hooks';
import { useMutateQuestions, useQuestionQuizzes } from '@/store';

const { TextArea } = Input;

const MODAL_WIDTH = 800;

export const ManualQuestionModal = () => {
  const { question, setQuestion, isOpen } = useManualQuestionForm();
  const [form] = Form.useForm<IQuestion>();
  const [correctAnswer, setCorrectAnswer] = useState(0);
  const [isFormulaEditorOpen, setIsFormulaEditorOpen] = useState(false);
  const { updateQuestion, createQuestion } = useMutateQuestions();
  const { t } = useTranslation(['DefaultTranslations', 'CommonQuizFormTranslations']);
  const formula = Form.useWatch('formula', form);
  const { quizzes } = useQuestionQuizzes(question?._id);
  const navigateTo = useNavigate();

  useEffect(() => {
    if (question) {
      form.setFieldsValue(question);
      setCorrectAnswer(question.correctAnswer);
      if (question.formula) {
        form.setFieldValue('formula', question.formula);
      }
      return;
    }
    form.setFieldValue('answers', [{ text: '' }]);
    setCorrectAnswer(0);
  }, [question]);

  const handleFinish = (values: IQuestion) => {
    const newQuestion = {
      ...values,
      correctAnswer,
      type: EQuestionType.MANUAL
    };
    if (question) {
      updateQuestion(newQuestion, { onSuccess: handleCancel });
    } else {
      createQuestion(newQuestion, {
        onSuccess: handleCancel
      });
    }
  };

  const handleCorrectAnswer = (value: number) => setCorrectAnswer(value);

  const handleCancel = () => {
    setQuestion();
    form.resetFields();
    form.setFieldValue('answers', [{ text: '' }]);
    setCorrectAnswer(0);
  };

  const handleInsertFormula = (html?: string) => form.setFieldValue('formula', html);

  const handleNavigateToQuiz = (quizId: string) => navigateTo(`/content/tests/${quizId}/edit`);

  const quizzesContainingQuestion = useMemo(
    () => [
      {
        key: 'quizzes',
        label: (
          <TitleWrapper>{t('CommonQuizFormTranslations:QUIZZES_CONTAINING_QUESTION')}</TitleWrapper>
        ),
        children: (
          <ul>
            {quizzes?.map(quiz => (
              <QuizItemWrapper onClick={() => handleNavigateToQuiz(quiz._id)} key={quiz._id}>
                {quiz.title}
              </QuizItemWrapper>
            ))}
          </ul>
        )
      }
    ],
    [quizzes]
  );

  return (
    <Modal
      width={MODAL_WIDTH}
      centered
      forceRender
      open={isOpen}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflow: 'auto'
        }
      }}
      onCancel={handleCancel}
      footer={
        <FooterWrapper>
          <Button onClick={handleCancel}>{t('DefaultTranslations:CANCEL')}</Button>
          <Button type="primary" onClick={form.submit}>
            {t('DefaultTranslations:SAVE')}
          </Button>
        </FooterWrapper>
      }
      title={t('CommonQuizFormTranslations:UPDATE_QUESTION')}>
      <Form form={form} onFinish={handleFinish} layout="vertical">
        {question && (
          <Alert
            message={t('CommonQuizFormTranslations:UPDATE_QUESTION_WARNING')}
            type="warning"
            showIcon
          />
        )}
        <Form.Item name="_id" hidden>
          <Input />
        </Form.Item>
        <Form.Item
          name="title"
          label={t('CommonQuizFormTranslations:QUESTION')}
          rules={[RULES.REQUIRED]}>
          <TextArea rows={2} />
        </Form.Item>
        <Form.Item noStyle name="formula">
          {formula && (
            <Wrapper>
              <FormulaWrapper dangerouslySetInnerHTML={{ __html: formula }} />
            </Wrapper>
          )}
        </Form.Item>
        <ButtonWrapper>
          <Form.Item name="image">
            <Upload
              preview
              mode="upload"
              label={t('CommonQuizFormTranslations:SELECT_IMAGE')}
              margin="auto"
              accept="image/*"
              icon="Image"
            />
          </Form.Item>
          <Button icon={<Icon i="Function" />} onClick={() => setIsFormulaEditorOpen(true)}>
            {t('CommonQuizFormTranslations:INSERT_FORMULA')}
          </Button>
          <InsertFormula
            isOpen={isFormulaEditorOpen}
            setIsOpen={setIsFormulaEditorOpen}
            onChange={f => handleInsertFormula(f)}
          />
        </ButtonWrapper>
        <ItemsWrapper>
          <Form.Item name="year" label={t('CommonQuizFormTranslations:YEAR')}>
            <InputNumber max={dayjs().year()} />
          </Form.Item>
          <Form.Item name="difficulty" label={t('CommonQuizFormTranslations:DIFFICULTY')}>
            <Radio.Group
              options={Object.values(EQuestionDifficulty).map(difficulty => ({
                label: t(`CommonQuizFormTranslations:${difficulty}`),
                value: difficulty
              }))}
            />
          </Form.Item>
        </ItemsWrapper>
        <Answers
          correctAnswer={correctAnswer}
          handleCorrectAnswer={handleCorrectAnswer}
          form={form}
        />
        <Form.Item
          name="explanation"
          label={
            <RowWrapper>
              {t('CommonQuizFormTranslations:EXPLANATION')}
              <Tooltip trigger="hover" title={t('CommonQuizFormTranslations:EXPLANATION_TOOLTIP')}>
                <div>
                  <Icon i="Information" />
                </div>
              </Tooltip>
            </RowWrapper>
          }>
          <TextArea rows={2} />
        </Form.Item>
      </Form>
      {question && <Collapse ghost items={quizzesContainingQuestion} />}
    </Modal>
  );
};
